import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { updateVariant } from '../../api/variants';
import { VariantInterface } from '../../types';

interface EditVariantDialogProps {
  open: boolean;
  variant: VariantInterface | null;
  onClose: () => void;
  onVariantUpdated: () => void;
}

interface EditData {
  name: string;
  price: string;
  quantity: string;
  available: boolean;
  active: boolean;
  details: string;
  is_unlimited: boolean;
}

function EditVariantDialog({ open, variant, onClose, onVariantUpdated }: EditVariantDialogProps): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<EditData>({
    name: '',
    price: '',
    quantity: '',
    available: true,
    active: true,
    details: '',
    is_unlimited: false,
  });

  useEffect(() => {
    if (variant) {
      setData({
        name: variant.name,
        price: variant.price?.toString() ?? '',
        quantity: variant.quantity?.toString() ?? '',
        available: !!variant.available,
        active: !!variant.active,
        details: variant.details ?? '',
        is_unlimited: !!variant.is_unlimited,
      });
    }
  }, [variant]);

  const handleChange = (field: keyof EditData) => (e: React.ChangeEvent<HTMLInputElement>): void => {
    setData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleCheckboxChange = (field: keyof EditData) => (e: React.ChangeEvent<HTMLInputElement>): void => {
    setData(prev => ({
      ...prev,
      [field]: e.target.checked
    }));
  };

  const handleUpdate = async (): Promise<void> => {
    if (!variant) return;
    setLoading(true);
    try {
      await updateVariant(variant.id, {
        name: data.name,
        price: data.price === '' ? undefined : Number(data.price),
        quantity: data.quantity === '' ? undefined : Number(data.quantity),
        available: data.available,
        active: data.active,
        details: data.details,
        is_unlimited: data.is_unlimited,
      });
      onVariantUpdated();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (): void => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>ערוך קורס</DialogTitle>
      <DialogContent>
        <TextField
          label="שם"
          value={data.name}
          onChange={handleChange('name')}
          required
          fullWidth
          sx={{ my: 2 }}
        />
        <TextField
          label="מחיר"
          type="number"
          value={data.price}
          onChange={handleChange('price')}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="כמות"
          type="number"
          value={data.quantity}
          onChange={handleChange('quantity')}
          fullWidth
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={<Checkbox checked={data.is_unlimited} onChange={handleCheckboxChange('is_unlimited')} />}
          label="כמות ללא הגבלה"
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={<Checkbox checked={data.available} onChange={handleCheckboxChange('available')} />}
          label="מופיע בחנות"
          sx={{ mb: 2 }}
          title="האם המוצר זמין לרכישה בחנות (אם לא, עדיין אפשר לרכוש דרך מחיר מיוחד ולינק לרכישה)"
        />
        <FormControlLabel
          control={<Checkbox checked={data.active} onChange={handleCheckboxChange('active')} />}
          label="פעיל"
          sx={{ mb: 2 }}
          title="האם הקורס פעיל או לא (אם לא, אי אפשר לרכוש בכלל)"

        />
        <TextField
          label="פרטים"
          value={data.details}
          onChange={handleChange('details')}
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>ביטול</Button>
        <Button onClick={handleUpdate} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'עדכן'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditVariantDialog; 